import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from "../../Context/Cartcontext";
import { useUser } from "@clerk/clerk-react";
import { useNav } from "../../Context/NavContext";
import './Checkout.css';

// Image utility function
const getImageUrl = (imageUrl) => {
  const BASE_URL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";
  if (!imageUrl) return '/placeholder-image.png';
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
  if (imageUrl.startsWith('/images/')) return `${BASE_URL}${imageUrl}`;
  return `${BASE_URL}/images/${imageUrl}`;
};

// ─────────────────────────────────────────────
// Card Payment Preview Component
// ─────────────────────────────────────────────
const CardPaymentPreview = ({ finalTotal, onPayClick, processing }) => (
  <div className="enhanced-stripe-form">
    <div className="card-input-section">
      <div className="card-input-header">
        <div className="accepted-cards">
          <span className="accepted-label">We accept:</span>
          <div className="card-brands">
            {['VISA', 'MC', 'AMEX', 'DISC'].map((brand) => (
              <div key={brand} className={`card-brand ${brand.toLowerCase()}`}>
                <span>{brand}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="secure-badge">
          <span className="secure-icon">🔒</span>
          <span className="secure-text">256-bit SSL</span>
        </div>
      </div>

      <div className="card-visual-only">
        <div className="card-mockup">
          <div className="card-chip"></div>
          <div className="card-brand-icon">💳</div>
          <div className="card-number-placeholder">•••• •••• •••• ••••</div>
          <div className="card-details">
            <div className="card-holder">CARD HOLDER</div>
            <div className="card-expiry">MM/YY</div>
          </div>
        </div>
      </div>

      <div className="payment-summary-card">
        <div className="summary-header"><h4>Payment Summary</h4></div>
        <div className="summary-content">
          <div className="amount-row">
            <span className="amount-label">Total Amount:</span>
            <span className="amount-value">{finalTotal.toLocaleString()} LKR</span>
          </div>
          <div className="currency-note">
            <span className="note-icon">ℹ️</span>
            <span className="note-text">Charged in Sri Lankan Rupees (LKR)</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onPayClick}
        disabled={processing}
        className={`enhanced-payment-btn ${processing ? 'processing' : ''}`}
      >
        <div className="btn-content">
          {processing ? (
            <>
              <div className="processing-spinner"></div>
              <span>Redirecting to PayHere...</span>
            </>
          ) : (
            <>
              <span className="payment-icon">💳</span>
              <span>Pay {finalTotal.toLocaleString()} LKR Securely</span>
            </>
          )}
        </div>
      </button>

      <div className="trust-indicators">
        {[
          { icon: '🔒', text: 'Secured by PayHere' },
          { icon: '⚡', text: 'Instant processing' },
          { icon: '↩️', text: 'Easy refunds' },
        ].map(({ icon, text }) => (
          <div key={text} className="trust-item">
            <span className="trust-icon">{icon}</span>
            <span className="trust-text">{text}</span>
          </div>
        ))}
      </div>

      <p className="payment-disclaimer">
        Your payment information is encrypted and secure. You will be redirected to
        PayHere payment gateway to complete your payment.
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Koko Payment Calculator Component (CALCULATION UNCHANGED)
// ─────────────────────────────────────────────
const KokoPaymentCalculator = ({ finalTotal, onSubmit, isSubmitting, onBack }) => {
  const [installmentPlan, setInstallmentPlan] = useState(3);

  const netAmount   = finalTotal;
  const grossAmount = netAmount / 0.88;
  const kokoCommission = grossAmount - netAmount;

  const calculateInstallments = (months) => {
    const installments = [];
    for (let i = 0; i < months; i++) {
      if (i < months - 1) {
        installments.push((grossAmount / months).toFixed(2));
      } else {
        const sumPrev = installments.reduce((acc, val) => acc + parseFloat(val), 0);
        installments.push((grossAmount - sumPrev).toFixed(2));
      }
    }
    return installments;
  };

  const installments = calculateInstallments(installmentPlan);

  return (
    <div className="koko-payment-calculator">
      <div className="koko-calculator-card">
        <h3 className="koko-title">📱 KOKO Installment Payment</h3>

        <div className="koko-calculation-section">
          <div className="calculation-header"><h4>Payment Breakdown</h4></div>
          <div className="calculation-rows">
            <div className="calc-row">
              <span className="calc-label">Net Amount (You Pay):</span>
              <span className="calc-value">Rs {netAmount.toFixed(2)}</span>
            </div>
            <div className="calc-row highlight">
              <span className="calc-label">KOKO Commission (12%):</span>
              <span className="calc-value">Rs {kokoCommission.toFixed(2)}</span>
            </div>
            <div className="calc-row total">
              <span className="calc-label">Total Payable via KOKO:</span>
              <span className="calc-value">Rs {grossAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="installment-plan-section">
          <label className="check-form-label">Select Installment Plan:</label>
          <div className="installment-options">
            {[3, 6].map((months) => (
              <label
                key={months}
                className={`installment-option ${installmentPlan === months ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="installmentPlan"
                  value={months}
                  checked={installmentPlan === months}
                  onChange={(e) => setInstallmentPlan(parseInt(e.target.value))}
                />
                <span className="option-content">
                  <span className="option-title">{months} Months</span>
                  <span className="option-amount">Rs {(grossAmount / months).toFixed(2)} / month</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="installment-breakdown">
          <h4 className="breakdown-title">Installment Schedule ({installmentPlan} Months)</h4>
          <div className="installment-list">
            {installments.map((amount, index) => (
              <div key={index} className="installment-item">
                <span className="installment-number">Installment {index + 1}</span>
                <span className="installment-amount">Rs {parseFloat(amount).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="koko-info-banner">
          <span className="info-icon">ℹ️</span>
          <p>You will be redirected to KOKO payment gateway to complete your installment payment.</p>
        </div>

        <div className="button-group">
          <button type="button" onClick={onBack} className="check-btn-secondary">← Back</button>
          <button
            type="button"
            onClick={() => onSubmit(installmentPlan, grossAmount)}
            disabled={isSubmitting}
            className={`check-btn-primary ${isSubmitting ? 'loading' : ''}`}
          >
            {isSubmitting ? '🔄 Processing...' : `Pay via KOKO (Rs ${grossAmount.toFixed(2)})`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Checkout Component
// ─────────────────────────────────────────────
const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  const { user, isLoaded, isSignedIn } = useUser();
  const { isAuthenticated, userInfo } = useNav();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [currentStep,    setCurrentStep]    = useState(1);
  const [formData,       setFormData]       = useState({
    userType: 'personal',
    fullName: '',
    companyName: '',
    vatNumber: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'card',
  });
  const [formErrors,     setFormErrors]     = useState({});
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [orderSuccess,   setOrderSuccess]   = useState(null);
  const [transferReceipt, setTransferReceipt] = useState(null);
  const [receiptPreview,  setReceiptPreview]  = useState(null);

  const payhereReady = useRef(false);

  const total      = cartItems.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
  const deliveryFee = 0;
  const finalTotal  = total;
  const baseURL     = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  // ── Pre-fill user info ──────────────────────
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
      setFormData((prev) => ({
        ...prev,
        fullName,
        email: user.primaryEmailAddress?.emailAddress || '',
      }));
    } else if (isAuthenticated && userInfo) {
      setFormData((prev) => ({
        ...prev,
        fullName: userInfo.name  || '',
        email:    userInfo.email || '',
      }));
    }
  }, [isLoaded, isSignedIn, user, isAuthenticated, userInfo]);

  // ── Guard: must arrive from cart ───────────
  useEffect(() => {
    if (!location.state?.fromCart) navigate('/');
  }, [location.state, navigate]);

  // ── Redirect after success ─────────────────
  useEffect(() => {
    if (orderSuccess) {
      clearAllCartData();
      const timer = setTimeout(() => { window.location.href = '/'; }, 3000);
      return () => clearTimeout(timer);
    }
  }, [orderSuccess]);

  // ── Load PayHere SDK ───────────────────────
  useEffect(() => {
    if (document.querySelector('script[src="https://www.payhere.lk/lib/payhere.js"]')) {
      payhereReady.current = true;
      return;
    }
    const script = document.createElement('script');
    script.src   = 'https://www.payhere.lk/lib/payhere.js';
    script.async = true;
    script.onload  = () => { payhereReady.current = true; };
    script.onerror = () => { console.error('Failed to load PayHere SDK'); };
    document.body.appendChild(script);

    return () => {
      const existing = document.querySelector('script[src="https://www.payhere.lk/lib/payhere.js"]');
      if (existing) document.body.removeChild(existing);
      payhereReady.current = false;
    };
  }, []);

  // ── Cart cleanup helper ────────────────────
  const clearAllCartData = () => {
    if (clearCart) clearCart();
    window.dispatchEvent(new CustomEvent('clearCart'));
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.toLowerCase().includes('cart')) localStorage.removeItem(key);
      });
    } catch (e) { console.warn('localStorage clear failed:', e); }
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.toLowerCase().includes('cart')) sessionStorage.removeItem(key);
      });
    } catch (e) { console.warn('sessionStorage clear failed:', e); }
  };

  // ── Validation ─────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (formData.userType === 'personal') {
      if (!formData.fullName.trim())    errors.fullName    = 'Full name is required';
    } else {
      if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
    }
    if (!formData.email.trim())  errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.phone.trim())  errors.phone = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) errors.phone = 'Phone must be 10 digits';
    if (!formData.address.trim())    errors.address    = 'Address is required';
    if (!formData.city.trim())       errors.city       = 'City is required';
    if (!formData.postalCode.trim()) errors.postalCode = 'Postal code is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setFormErrors((prev) => ({ ...prev, receipt: 'File size must be less than 5MB' }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setFormErrors((prev) => ({ ...prev, receipt: 'Please upload an image file' }));
      return;
    }
    setTransferReceipt(file);
    setFormErrors((prev) => ({ ...prev, receipt: '' }));
    const reader = new FileReader();
    reader.onloadend = () => setReceiptPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleNext = () => { if (validateForm()) setCurrentStep(2); };

  // ── Create order ───────────────────────────
  const createOrder = async (kokoData = null, paymentStatus = 'pending') => {
    try {
      const formDataToSend = new FormData();

      formDataToSend.append('customerInfo', JSON.stringify({
        userType:    formData.userType,
        fullName:    formData.fullName,
        companyName: formData.companyName,
        vatNumber:   formData.vatNumber,
        email:       formData.email,
        phone:       formData.phone.replace(/\D/g, ''),
        address:     formData.address,
        city:        formData.city,
        postalCode:  formData.postalCode,
      }));

      formDataToSend.append('items', JSON.stringify(
        cartItems.map((item) => ({
          productId: item._id || item.id,
          name:      item.name,
          price:     item.price,
          quantity:  item.quantity || 1,
          imageUrl:  item.imageUrl,
        }))
      ));

      formDataToSend.append('paymentMethod', formData.paymentMethod);
      formDataToSend.append('paymentStatus', paymentStatus);

      if (kokoData) {
        formDataToSend.append('kokoInstallmentPlan', kokoData.installmentPlan);
        formDataToSend.append('kokoGrossAmount',     kokoData.grossAmount);
        formDataToSend.append('kokoCommission',      kokoData.commission);
        formDataToSend.append('subtotal',            total);
        formDataToSend.append('deliveryFee',         deliveryFee);
        formDataToSend.append('total',               kokoData.grossAmount);
      } else {
        formDataToSend.append('subtotal',    total);
        formDataToSend.append('deliveryFee', deliveryFee);
        formDataToSend.append('total',       finalTotal);
      }

      if (formData.paymentMethod === 'bank' && transferReceipt) {
        formDataToSend.append('transferReceipt', transferReceipt);
      }

      const response = await fetch(`${baseURL}/api/order/createorder`, {
        method: 'POST',
        body:   formDataToSend,
      });

      const result = await response.json();
      if (response.ok) return { success: true,  data: result };
      return        { success: false, error: result.message || 'Failed to create order' };
    } catch (error) {
      console.error('Error creating order:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // ── PayHere Payment ────────────────────────
  const initiatePayHerePayment = async () => {
    if (!payhereReady.current || !window.payhere) {
      alert('Payment gateway is still loading. Please wait a moment and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const orderResult = await createOrder(null, 'pending');

      if (!orderResult.success) {
        alert(`Order failed: ${orderResult.error}`);
        setIsSubmitting(false);
        return;
      }

      const orderId  = orderResult.data.orderNumber || orderResult.data.order?.orderNumber;
      const amount   = finalTotal.toFixed(2);
      const currency = 'LKR';

      const hashResponse = await fetch(
        `${baseURL}/api/order/payhere-hash?order_id=${encodeURIComponent(orderId)}&amount=${encodeURIComponent(amount)}&currency=${encodeURIComponent(currency)}`
      );

      if (!hashResponse.ok) {
        alert('Payment setup failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const hashData = await hashResponse.json();

      if (!hashData.success || !hashData.hash || !hashData.merchant_id) {
        alert('Payment setup failed: invalid hash response. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const nameParts = formData.fullName.split(' ');
      const firstName = formData.userType === 'personal' ? (nameParts[0] || '') : formData.companyName;
      const lastName  = formData.userType === 'personal' ? nameParts.slice(1).join(' ') : '';

      const payment = {
        sandbox:     false,
        merchant_id: hashData.merchant_id,
        return_url:  `${window.location.origin}/payment-success`,
        cancel_url:  `${window.location.origin}/checkout`,
        notify_url:  `${baseURL}/api/order/payhere-notify`,
        order_id:    orderId,
        items:       cartItems.map((item) => item.name).join(', '),
        amount,
        currency,
        hash:        hashData.hash,
        first_name:  firstName,
        last_name:   lastName,
        email:       formData.email,
        phone:       formData.phone.replace(/\D/g, ''),
        address:     formData.address,
        city:        formData.city,
        country:     'Sri Lanka',
      };

      window.payhere.onCompleted = function (completedOrderId) {
        console.log('Payment completed. OrderID:', completedOrderId);
        setOrderSuccess({ orderId: completedOrderId });
        clearAllCartData();
      };

      window.payhere.onDismissed = function () {
        console.log('Payment dismissed by user');
        setIsSubmitting(false);
      };

      window.payhere.onError = function (error) {
        console.error('PayHere error:', error);
        alert('Payment failed. Please try again.');
        setIsSubmitting(false);
      };

      window.payhere.startPayment(payment);

    } catch (error) {
      console.error('Payment initiation error:', error);
      alert('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────
  // KOKO PAYMENT — REAL API INTEGRATION
  // (calculation UNCHANGED — uses grossAmount from KokoPaymentCalculator)
  // ─────────────────────────────────────────────
  // Flow:
  //   1. Create order in our DB as 'pending' (sends kokoData incl. grossAmount)
  //   2. Ask backend /api/order/koko-initiate to build a signed Koko payload
  //      (RSA private key stays server-side — NEVER in the browser)
  //   3. Backend returns { endpoint, payload }
  //   4. We auto-POST a hidden HTML form to Koko's gateway
  //   5. Koko hits our _responseUrl (server-to-server) to confirm payment
  //   6. Koko redirects user to _returnUrl on success or _cancelUrl on cancel
  // ─────────────────────────────────────────────
  const handleKokoPayment = async (installmentPlan, grossAmount) => {
    setIsSubmitting(true);

    try {
      const kokoData = {
        installmentPlan,
        grossAmount,
        commission: grossAmount - finalTotal,
      };

      // Step 1: create the order in our DB as pending
      const orderResult = await createOrder(kokoData, 'pending');

      if (!orderResult.success) {
        alert(`Order failed: ${orderResult.error}`);
        setIsSubmitting(false);
        return;
      }

      const orderId =
        orderResult.data.orderNumber ||
        orderResult.data.order?.orderNumber ||
        orderResult.data._id;

      // Step 2: ask our backend to build the Koko-signed payload.
      // Send grossAmount (12% commission added) — this is what Koko charges
      // the customer. The original calculation logic is untouched.
      const initResponse = await fetch(`${baseURL}/api/order/koko-initiate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId,
          amount:      grossAmount.toFixed(2),
          currency:    'LKR',
          description: cartItems.map((item) => item.name).join(', ').slice(0, 200),
          firstName:
            formData.userType === 'personal'
              ? (formData.fullName.split(' ')[0] || '')
              : formData.companyName,
          lastName:
            formData.userType === 'personal'
              ? formData.fullName.split(' ').slice(1).join(' ')
              : '',
          email:           formData.email,
          installmentPlan,
        }),
      });

      if (!initResponse.ok) {
        const err = await initResponse.json().catch(() => ({}));
        alert(`KOKO setup failed: ${err.message || 'Please try again.'}`);
        setIsSubmitting(false);
        return;
      }

      const initData = await initResponse.json();

      // Expected backend response shape:
      // {
      //   success:  true,
      //   endpoint: 'https://qaapi.paykoko.com/api/merchants/orderCreate',
      //   payload:  { _mId, api_key, _returnUrl, _cancelUrl, _responseUrl,
      //               _amount, _currency, _reference, _orderId, _pluginName,
      //               _pluginVersion, _description, _firstName, _lastName,
      //               _email, dataString, signature }
      // }
      if (!initData.success || !initData.payload || !initData.endpoint) {
        alert('KOKO setup failed: invalid response from server.');
        setIsSubmitting(false);
        return;
      }

      // Step 3: build a hidden form and POST it to Koko's gateway.
      // Koko expects application/x-www-form-urlencoded — exactly what a
      // native <form method="POST"> submission produces.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = initData.endpoint;
      form.style.display = 'none';

      Object.entries(initData.payload).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = key;
        input.value = value == null ? '' : String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      // Browser navigates away to Koko's gateway — nothing else runs.
    } catch (error) {
      console.error('KOKO payment error:', error);
      alert('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ── Bank Transfer Submit ───────────────────
  const handleBankSubmit = async () => {
    if (!validateForm()) return;
    if (!transferReceipt) {
      setFormErrors((prev) => ({ ...prev, receipt: 'Please upload transfer receipt' }));
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createOrder(null, 'pending');
      if (result.success) {
        setOrderSuccess(result.data);
        clearAllCartData();
      } else {
        alert(`Order failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Bank transfer submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Shipping Info', icon: '📋' },
    { number: 2, title: 'Payment',       icon: '💳' },
  ];

  // ── Success Screen ─────────────────────────
  if (orderSuccess) {
    return (
      <div className="checkout-container">
        <div className="checkout-wrapper">
          <div className="success-screen">
            <div className="success-icon">✓</div>
            <h1>Order Successful!</h1>
            <p>Thank you for your order. A confirmation email is on its way.</p>
            <p>Redirecting to home page in 3 seconds…</p>
            <button
              onClick={() => { clearAllCartData(); window.location.href = '/'; }}
              className="check-btn-primary"
            >
              Shop More
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Render ────────────────────────────
  return (
    <div className="checkout-container">
      <div className="checkout-wrapper">
        <div className="checkout-header">
          <h1 className="checkout-title">Checkout</h1>
          <p className="checkout-subtitle">Finalize your purchase</p>
        </div>

        {/* Progress Steps */}
        <div className="progress-container">
          {steps.map((step, index) => (
            <div key={step.number} className="progress-step-wrapper">
              <div className={`progress-step ${currentStep >= step.number ? 'active' : ''}`}>
                <span className="step-icon">{step.icon}</span>
                <span className="step-title">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`progress-line ${currentStep > step.number ? 'active' : ''}`} />
              )}
            </div>
          ))}
        </div>

        <div className="checkout-content">
          {/* ── Left: Form ── */}
          <div className="checkout-form-section">

            {/* Step 1: Shipping */}
            {currentStep === 1 && (
              <div>
                <h2 className="section-title">📋 Shipping Details</h2>
                <div className="check-form-grid">

                  {/* Account type */}
                  <div className="check-form-group">
                    <label className="check-form-label">Account Type</label>
                    <div className="check-account-type-selector">
                      {[
                        { value: 'personal', icon: '👤', label: 'Personal' },
                        { value: 'company',  icon: '🏢', label: 'Company'  },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className={`check-account-type-option ${formData.userType === opt.value ? 'check-selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="userType"
                            value={opt.value}
                            checked={formData.userType === opt.value}
                            onChange={handleChange}
                          />
                          <span className="check-payment-icon">{opt.icon}</span>
                          <span className="check-payment-label">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Personal name */}
                  {formData.userType === 'personal' && (
                    <div className="check-form-group">
                      <label className="check-form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={`check-form-input ${formErrors.fullName ? 'check-error' : ''}`}
                      />
                      {formErrors.fullName && <span className="check-error-message">{formErrors.fullName}</span>}
                    </div>
                  )}

                  {/* Company fields */}
                  {formData.userType === 'company' && (
                    <>
                      <div className="check-form-group">
                        <label className="check-form-label">Company Name *</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          className={`check-form-input ${formErrors.companyName ? 'check-error' : ''}`}
                        />
                        {formErrors.companyName && <span className="check-error-message">{formErrors.companyName}</span>}
                      </div>
                      <div className="check-form-group">
                        <label className="check-form-label">VAT Number (Optional)</label>
                        <input
                          type="text"
                          name="vatNumber"
                          value={formData.vatNumber}
                          onChange={handleChange}
                          className="check-form-input"
                        />
                      </div>
                    </>
                  )}

                  {/* Email + Phone */}
                  <div className="check-form-row">
                    <div className="check-form-group">
                      <label className="check-form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`check-form-input ${formErrors.email ? 'check-error' : ''}`}
                      />
                      {formErrors.email && <span className="check-error-message">{formErrors.email}</span>}
                    </div>
                    <div className="check-form-group">
                      <label className="check-form-label">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0771234567"
                        className={`check-form-input ${formErrors.phone ? 'check-error' : ''}`}
                      />
                      {formErrors.phone && <span className="check-error-message">{formErrors.phone}</span>}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="check-form-group">
                    <label className="check-form-label">Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Enter full address..."
                      className={`check-form-textarea ${formErrors.address ? 'check-error' : ''}`}
                    />
                    {formErrors.address && <span className="check-error-message">{formErrors.address}</span>}
                  </div>

                  {/* City + Postal */}
                  <div className="check-form-row">
                    <div className="check-form-group">
                      <label className="check-form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`check-form-input ${formErrors.city ? 'check-error' : ''}`}
                      />
                      {formErrors.city && <span className="check-error-message">{formErrors.city}</span>}
                    </div>
                    <div className="check-form-group">
                      <label className="check-form-label">Postal Code *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        placeholder="10400"
                        className={`check-form-input ${formErrors.postalCode ? 'check-error' : ''}`}
                      />
                      {formErrors.postalCode && <span className="check-error-message">{formErrors.postalCode}</span>}
                    </div>
                  </div>

                  <button type="button" onClick={handleNext} className="check-btn-primary check-btn-full">
                    Proceed to Payment →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Payment */}
            {currentStep === 2 && (
              <div>
                <h2 className="section-title">💳 Payment Options</h2>

                <div className="payment-methods">
                  {[
                    { value: 'card', label: 'Credit/Debit Card', icon: '💳' },
                    { value: 'bank', label: 'Bank Transfer',      icon: '🏦' },
                    { value: 'koko', label: 'Koko Payment',       icon: '📱' },
                  ].map((method) => (
                    <label
                      key={method.value}
                      className={`payment-method ${formData.paymentMethod === method.value ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.value}
                        checked={formData.paymentMethod === method.value}
                        onChange={handleChange}
                      />
                      <span className="payment-icon">{method.icon}</span>
                      <span className="payment-label">{method.label}</span>
                    </label>
                  ))}
                </div>

                {/* Card */}
                {formData.paymentMethod === 'card' && (
                  <CardPaymentPreview
                    finalTotal={finalTotal}
                    onPayClick={initiatePayHerePayment}
                    processing={isSubmitting}
                  />
                )}

                {/* Bank Transfer */}
                {formData.paymentMethod === 'bank' && (
                  <div className="bank-transfer-section">
                    <div className="bank-details-card">
                      <h3 className="bank-details-title">🏦 Bank Account Details</h3>
                      <div className="bank-info-grid">
                        {[
                          { label: 'Bank Name',       value: 'Sampath Bank PLC'  },
                          { label: 'Account Name',    value: 'Properteck'        },
                          { label: 'Account Number',  value: '122130002489'      },
                          { label: 'Branch',          value: 'Peliyagoda Branch' },
                        ].map(({ label, value }) => (
                          <div key={label} className="bank-info-item">
                            <span className="bank-info-label">{label}:</span>
                            <span className="bank-info-value">{value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="bank-instructions">
                        <p className="instruction-text">
                          ℹ️ Please transfer <strong>{finalTotal.toLocaleString()} LKR</strong> to the above account
                          and upload the transfer receipt below.
                        </p>
                      </div>
                    </div>

                    <div className="receipt-upload-section">
                      <label className="check-form-label">Upload Transfer Receipt *</label>
                      <div className="receipt-upload-container">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleReceiptUpload}
                          className="receipt-input"
                          id="receipt-upload"
                        />
                        <label htmlFor="receipt-upload" className="receipt-upload-label">
                          <span className="upload-icon">📎</span>
                          <span className="upload-text">
                            {transferReceipt ? transferReceipt.name : 'Choose file or drag here'}
                          </span>
                          <span className="upload-hint">PNG, JPG up to 5MB</span>
                        </label>
                      </div>
                      {formErrors.receipt && <span className="check-error-message">{formErrors.receipt}</span>}
                      {receiptPreview && (
                        <div className="receipt-preview">
                          <p className="preview-label">Receipt Preview:</p>
                          <img src={receiptPreview} alt="Receipt preview" className="preview-image" />
                          <button
                            type="button"
                            onClick={() => { setTransferReceipt(null); setReceiptPreview(null); }}
                            className="remove-receipt-btn"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="button-group">
                      <button type="button" onClick={() => setCurrentStep(1)} className="check-btn-secondary">
                        ← Back
                      </button>
                      <button
                        type="button"
                        onClick={handleBankSubmit}
                        disabled={isSubmitting || !transferReceipt}
                        className={`check-btn-primary ${isSubmitting ? 'loading' : ''}`}
                      >
                        {isSubmitting ? '🔄 Processing...' : `Submit Order (${finalTotal.toLocaleString()} LKR)`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Koko */}
                {formData.paymentMethod === 'koko' && (
                  <KokoPaymentCalculator
                    finalTotal={finalTotal}
                    onSubmit={handleKokoPayment}
                    isSubmitting={isSubmitting}
                    onBack={() => setCurrentStep(1)}
                  />
                )}
              </div>
            )}
          </div>

          {/* ── Right: Order Summary ── */}
          <div className="order-summary-section">
            <h3 className="summary-title">🛍 Order Summary</h3>
            <div className="order-items">
              {cartItems.map((item) => (
                <div key={item.id || item._id} className="order-item">
                  <div className="item-image">
                    <img src={getImageUrl(item.imageUrl)} alt={item.name} />
                  </div>
                  <div className="item-info">
                    <h4 className="item-name">{item.name}</h4>
                    <p className="item-quantity">Qty: {item.quantity || 1}</p>
                    <p className="item-price">{(item.price * (item.quantity || 1)).toLocaleString()} LKR</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>{total.toLocaleString()} LKR</span>
              </div>
              <div className="total-final">
                <span>Total:</span>
                <span>{finalTotal.toLocaleString()} LKR</span>
              </div>
            </div>
            <div className="security-badge">
              <p>🔒 Secured with SSL encryption</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;